import {
  Box,
  Button,
  Flex,
  HStack,
  MenuButton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { SFListAddIcon } from '@assets/icons';
import { useQueryStore, useRefStore, useUserStore } from '@store/app.store';
import { useDebouncedProp, useFeedPageFilters, useSetDocumentTitle } from '@util/hooks';
import { useInfiniteCards } from '@services/query-services/cards.query-service';
import { getAnalyzeTooltipText, isBulkExportDisabled } from '@util/helpers/general.helpers';
import FeedFilterSidebar from '@components/feed/feed-filter-sidebar.component';
import FeedFilterMobile from '@components/feed/feed-filter-mobile.component';
import FeedSummaries from '@components/feed/feed-summaries.component';
import ColumnedLayout from '@layouts/columned.layout';
import ReskinMobileStickyNav from '@components/general/reskin-mobile-sticky-nav.component';
import AgendaHeader from '@components/agenda/agenda-header.component';
import ExplorerAnalyzeButton from '@components/explorer/explorer-analyze-button.component';
import BulkExportModal from '@components/general/modals/bulk-export-modal.component';
import CollectionsMenu from '@components/collection/collections-menu.component';

export default function ReskinSummariesPage() {
  const tab = 'summaries';
  useSetDocumentTitle('Summaries - FORA');
  const user = useUserStore((state) => state.user);
  const setFeedCardsSearchBody = useQueryStore((state) => state.setFeedCardsSearchBody);
  const { filters, hasActiveFilters } = useFeedPageFilters();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { checkGate } = useStatsigClient();
  const enableCollections = checkGate('collections');

  const [debouncedKeyword] = useDebouncedProp(filters?.keyword);
  const defaultSearchBody = {
    has_source: true,
    person_id: user?.id,
    preferred: filters?.savedFilter ? true : undefined,
    pageSize: 8,
    initiative_ids: filters?.focuses?.length ? filters.focuses : undefined,
    label_ids: filters?.labels?.length ? filters.labels : undefined,
    participants_id: filters.participantId ? filters.participantId : undefined
  };

  const cardSearchBody = {
    ...defaultSearchBody,
    query: debouncedKeyword ? `${debouncedKeyword}*` : undefined,
    archived: !!filters?.archivedFilter,
    has_sub_source: filters?.contentTypeSubSources?.length ? true : undefined,
    sub_sources: filters?.contentTypeSubSources?.length ? filters.contentTypeSubSources : undefined,
    created_at_from: filters?.createdAtFrom || undefined,
    created_at_to: filters?.createdAtTo || undefined,
    participants_name: filters?.participantName || undefined,
    participants_emails: filters?.participantEmail ? [filters?.participantEmail] : undefined,
    participants_company_name: filters?.participantCompanyName || undefined,
    participants_domains: filters?.participantCompanyDomain
      ? [filters?.participantCompanyDomain]
      : undefined,
    pageSize: 25
  };

  const { data: cards } = useInfiniteCards(cardSearchBody, {
    enabled: !!user?.id,
    refetchOnMount: false,
    select: (data) => getCardsFromPages(data.pages)
  });

  useEffect(() => {
    setFeedCardsSearchBody(cardSearchBody);
  }, [cardSearchBody]);

  const addToCollectionActionText = 'add cards to a collection';

  return (
    <ColumnedLayout
      rightRailContent={<FeedFilterSidebar tab={tab} />}
      mobileContent={
        <MobileSummariesPage
          filters={filters}
          cardSearchBody={cardSearchBody}
          tab={tab}
        />
      }
    >
      <Box p={11}>
        <HStack
          justifyContent='space-between'
          mb={5}
        >
          {!hasActiveFilters && (
            <Text
              variant='page-header'
              data-testid='page-header'
            >
              Recent Updates
            </Text>
          )}
          {hasActiveFilters && (
            <Text
              variant='page-header'
              data-testid='page-header'
            >
              <Text
                as='span'
                mr={4}
              >
                Filtered Results
              </Text>

              <Text
                as='span'
                variant='body-600-md'
              >
                {cards?.length ? ` ${cards.length} items` : ''}
              </Text>
            </Text>
          )}
          <HStack>
            <Tooltip
              isDisabled={!getAnalyzeTooltipText('analyze summary cards', hasActiveFilters, cards)}
              label={getAnalyzeTooltipText('analyze summary cards', hasActiveFilters, cards)}
            >
              <Box>
                <ExplorerAnalyzeButton
                  isDisabled={!hasActiveFilters || !cards?.length}
                  sourcesToSet={cards}
                />
              </Box>
            </Tooltip>
            <BulkExportModal
              cards={cards}
              isDisabled={isBulkExportDisabled(hasActiveFilters, cards)}
              tooltipText={getAnalyzeTooltipText('bulk export', hasActiveFilters, cards)}
            />
            {enableCollections && (
              <Tooltip
                isDisabled={
                  !getAnalyzeTooltipText(addToCollectionActionText, hasActiveFilters, cards)
                }
                label={getAnalyzeTooltipText(addToCollectionActionText, hasActiveFilters, cards)}
              >
                <Box>
                  <CollectionsMenu
                    cards={cards}
                    menuButton={
                      <MenuButton
                        as={Button}
                        isDisabled={!hasActiveFilters || !cards?.length}
                        variant='reskinGhost'
                        leftIcon={<SFListAddIcon boxSize={6} />}
                        size='md'
                      >
                        Collection
                      </MenuButton>
                    }
                  />
                </Box>
              </Tooltip>
            )}
          </HStack>
        </HStack>
        <Stack gap={8}>
          {!hasActiveFilters && (
            <AgendaHeader
              date={selectedDate}
              setDate={setSelectedDate}
              showCalendar={false}
            />
          )}
          <FeedSummaries
            filters={filters}
            cardSearchBody={cardSearchBody}
          />
        </Stack>
      </Box>
    </ColumnedLayout>
  );
}

function MobileSummariesPage({ cardSearchBody, tab }) {
  const [isSelectingFilters, setIsSelectingFilters] = useState(false);
  const { handleResetFilters, filters } = useFeedPageFilters();
  const mainRef = useRefStore((state) => state.mainRef);

  const handleTabChange = (index) => {
    if (index === 1) {
      setIsSelectingFilters(true);
    } else {
      setIsSelectingFilters(false);
    }
  };

  const handleRecentClicked = () => {
    setIsSelectingFilters(false);
    handleResetFilters();
  };

  const handleFiltersClicked = () => {
    if (!isSelectingFilters) {
      setIsSelectingFilters(true);
    }
  };

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [isSelectingFilters]);

  return (
    <Flex flexDir='column'>
      <Tabs
        variant='navDark'
        onChange={handleTabChange}
      >
        <ReskinMobileStickyNav showCalendar={false}>
          <TabList
            justifyContent='space-between'
            w='full'
          >
            <Tab onClick={handleRecentClicked}>Recent Updates</Tab>
            <Tab onClick={handleFiltersClicked}>Filters</Tab>
          </TabList>
        </ReskinMobileStickyNav>
        <TabPanels
          maxW='720px'
          m='0 auto'
        >
          <TabPanel>
            <FeedSummaries
              filters={filters}
              cardSearchBody={cardSearchBody}
              p={2}
            />
          </TabPanel>
          <TabPanel>
            {isSelectingFilters && (
              <FeedFilterMobile
                setIsSelectingFilters={setIsSelectingFilters}
                tab={tab}
              />
            )}
            {!isSelectingFilters && (
              <FeedSummaries
                filters={filters}
                cardSearchBody={cardSearchBody}
                p={2}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}

function getCardsFromPages(pages) {
  return pages.flatMap((page) => page.data);
}
